import reduceReducers from "reduce-reducers";

/**
 * A reducer for fetching a user's composer tokens.
 *
 * @param {Object} state The previous state of the store.
 * @param {Object} action The action that just occurred.
 * @returns {Object} The new state of the store.
 */
import {
	CREATE_COMPOSER_TOKEN_FAILURE,
	CREATE_COMPOSER_TOKEN_REQUEST,
	CREATE_COMPOSER_TOKEN_SUCCESS,
	FETCH_COMPOSER_TOKENS_FAILURE,
	FETCH_COMPOSER_TOKENS_REQUEST,
	FETCH_COMPOSER_TOKENS_SUCCESS,
	RENAME_COMPOSER_TOKEN_FAILURE,
	RENAME_COMPOSER_TOKEN_REQUEST,
	RENAME_COMPOSER_TOKEN_SUCCESS,
	DELETE_COMPOSER_TOKEN_FAILURE,
	DELETE_COMPOSER_TOKEN_REQUEST,
	DELETE_COMPOSER_TOKEN_SUCCESS,
} from "shared-frontend/redux/actions/composerTokens";

import {
	CREATE_TOKEN_MODAL_OPEN,
	CREATE_TOKEN_MODAL_CLOSED,
	MANAGE_TOKEN_MODAL_CLOSED,
	MANAGE_TOKEN_MODAL_OPEN,
} from "../actions/composerTokens";

/**
 * Initial state
 */
const rootState = {
	composerTokens: {
		retrievingComposerTokens: false,
		creatingComposerToken: false,
		renamingComposerToken: false,
		disablingComposerToken: false,
		createTokenModalIsOpen: false,
		manageTokenModalIsOpen: false,
		manageTokenData: null,
		tokenError: null,
		tokenDeleted: false,
	},
};

/**
 * Reducers
 */

/**
 * A reducer for the composerTokens object within the ui object.
 *
 * @param {Object} state The current state of the object.
 * @param {Object} action The current action received.
 * @returns {Object} The updated ComposerTokens object.
 */
export function uiFetchComposerTokensReducer( state = rootState.composerTokens, action ) {
	switch ( action.type ) {
		case FETCH_COMPOSER_TOKENS_REQUEST:
			return Object.assign( {}, state, {
				retrievingComposerTokens: true,
				tokenError: null,
			} );
		case FETCH_COMPOSER_TOKENS_SUCCESS:
			return Object.assign( {}, state, {
				retrievingComposerTokens: false,
				tokenError: null,
			} );
		case FETCH_COMPOSER_TOKENS_FAILURE:
			return Object.assign( {}, state, {
				retrievingComposerTokens: false,
				tokenError: action.error,
			} );
		default:
			return state;
	}
}

/**
 * A reducer for the composerTokens object within the ui object.
 *
 * @param {Object} state The current state of the object.
 * @param {Object} action The current action received.
 * @returns {Object} The updated ComposerTokens object.
 */
export function uiCreateComposerTokensReducer( state = rootState.composerTokens, action ) {
	switch ( action.type ) {
		case CREATE_COMPOSER_TOKEN_REQUEST:
			return Object.assign( {}, state, {
				creatingComposerToken: true,
				tokenError: null,
			} );
		case CREATE_COMPOSER_TOKEN_SUCCESS:
			return Object.assign( {}, state, {
				creatingComposerToken: false,
			} );
		case CREATE_COMPOSER_TOKEN_FAILURE:
			return Object.assign( {}, state, {
				creatingComposerToken: false,
				tokenError: action.error,
			} );
		default:
			return state;
	}
}

/**
 * A reducer for the composerTokens object within the ui object.
 *
 * @param {Object} state The current state of the object.
 * @param {Object} action The current action received.
 * @returns {Object} The updated ComposerTokens object.
 */
export function uiRenameComposerTokensReducer( state = rootState.composerTokens, action ) {
	switch ( action.type ) {
		case RENAME_COMPOSER_TOKEN_REQUEST:
			return Object.assign( {}, state, {
				renamingComposerToken: true,
				tokenError: null,
			} );
		case RENAME_COMPOSER_TOKEN_SUCCESS:
			return Object.assign( {}, state, {
				renamingComposerToken: false,
			} );
		case RENAME_COMPOSER_TOKEN_FAILURE:
			return Object.assign( {}, state, {
				renamingComposerToken: false,
				tokenError: action.error,
			} );
		default:
			return state;
	}
}

/**
 * A reducer for the composerTokens object within the ui object.
 *
 * @param {Object} state The current state of the object.
 * @param {Object} action The current action received.
 * @returns {Object} The updated ComposerTokens object.
 */
export function uiDeleteComposerTokensReducer( state = rootState.composerTokens, action ) {
	switch ( action.type ) {
		case DELETE_COMPOSER_TOKEN_REQUEST:
			return Object.assign( {}, state, {
				disablingComposerToken: true,
				tokenError: null,
				tokenDeleted: false,
			} );
		case DELETE_COMPOSER_TOKEN_SUCCESS:
			return Object.assign( {}, state, {
				disablingComposerToken: false,
				tokenDeleted: true,
			} );
		case DELETE_COMPOSER_TOKEN_FAILURE:
			return Object.assign( {}, state, {
				disablingComposerToken: false,
				tokenError: action.error,
				tokenDeleted: false,
			} );
		default:
			return state;
	}
}

/* eslint-disable complexity */
/**
 * A reducer for the composerTokens object within the ui object.
 *
 * @param {Object} state The current state of the object.
 * @param {Object} action The current action received.
 * @returns {Object} The updated ComposerTokens object.
 */
export function uiModalsComposerTokensReducer( state = rootState.composerTokens, action ) {
	switch ( action.type ) {
		case CREATE_TOKEN_MODAL_OPEN:
			return Object.assign( {}, state, {
				createTokenModalIsOpen: true,
				tokenDeleted: false,
			} );
		case CREATE_TOKEN_MODAL_CLOSED:
			return Object.assign( {}, state, {
				createTokenModalIsOpen: false,
				tokenError: null,
			} );
		case MANAGE_TOKEN_MODAL_OPEN:
			return Object.assign( {}, state, {
				manageTokenModalIsOpen: true,
				manageTokenData: action.data,
				tokenDeleted: false,
			} );
		case MANAGE_TOKEN_MODAL_CLOSED:
			return Object.assign( {}, state, {
				manageTokenModalIsOpen: false,
				manageTokenData: null,
				tokenError: null,
			} );
		default:
			return state;
	}
}

const uiComposerTokensState = reduceReducers(
	uiFetchComposerTokensReducer,
	uiCreateComposerTokensReducer,
	uiRenameComposerTokensReducer,
	uiDeleteComposerTokensReducer,
	uiModalsComposerTokensReducer,
);
/* eslint-enable complexity */

/**
 * A combineReducer for the user object.
 *
 * @param {Object} state The current state of the object.
 * @param {Object} action The current action received.
 *
 * @returns {Object} The updated email string.
 */
export function uiComposerTokensReducer( state = rootState.composerTokens, action ) {
	return uiComposerTokensState( state, action );
}

